import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  GlobalStyles,
  IconButton,
  Stack,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Papa from 'papaparse';
import { useRef } from 'react';
import React from 'react';
import * as XLSX from 'xlsx';

import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';
import type { Unit } from '@/Units/types';

const useStyles = makeStyles({
  paper: {
    minHeight: 560,
    borderRadius: 10,
  },
});

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    body: { structure_id: string; units: Array<{ name: string; unit_flags: string[] }> },
    fileType: string,
  ) => Promise<void>;
  loading?: boolean;
  existingUnits: Unit[];
  structureId: string;
};

export const FormBulkUnit = ({
  open,
  onClose,
  onSubmit,
  loading: _loading = false,
  existingUnits,
  structureId,
}: Props) => {
  const classes = useStyles();
  const [isWrongData, setIsWrongData] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [fileType, setFileType] = React.useState('');
  const [validationErrors, setValidationErrors] = React.useState<string[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    const file = event.target.files?.[0];
    if (file) {
      const extension = file.name.split('.').pop()?.toLowerCase();

      if (extension === 'csv') {
        setFileType('CSV');
        handleCsvFile(file);
      } else if (extension === 'xlsx') {
        setFileType('XLSX');
        handleXlsxFile(file);
      } else {
        setIsWrongData(true);
        setIsLoading(false);
        resetFileInput();

        if (extension === 'numbers') {
          setValidationErrors([
            'Mac Numbers files (.numbers) are not supported.',
            'Please export your Numbers file as CSV or Excel format:',
            '1. Open your file in Numbers',
            '2. Go to File → Export To → CSV (or Excel)',
            '3. Save the exported file',
            '4. Upload the CSV or Excel file here',
          ]);
        } else {
          setValidationErrors([
            `File type ".${extension}" is not supported.`,
            'Please upload a CSV (.csv) or Excel (.xlsx) file.',
          ]);
        }
      }
    }
  };

  const handleCsvFile = (file: File) => {
    Papa.parse(file, {
      header: true,
      complete: async (results) => {
        processData(results.data);
      },
      error: (_error) => {
        setIsWrongData(true);
        setIsLoading(false);
        resetFileInput();
      },
    });
  };

  const handleXlsxFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target) {
        const data = new Uint8Array(event.target.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData: any[] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (jsonData.length === 0) {
          console.error('The file is empty.');
          setIsLoading(false);
          resetFileInput();
          return;
        }

        const headers: string[] = jsonData[0] as string[];
        const parsedData = jsonData.slice(1).map((row: any[]) => {
          const rowData: any = {};
          headers.forEach((header: string, index: number) => {
            const cellValue = row[index];
            rowData[header] =
              cellValue === undefined || cellValue === null ? '' : String(cellValue);
          });
          return rowData;
        });

        processData(parsedData);
      } else {
        setIsWrongData(true);
        setIsLoading(false);
        resetFileInput();
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleClose = () => {
    setIsWrongData(false);
    setValidationErrors([]);
    setFileType('');
    setIsLoading(false);
    resetFileInput();
    onClose();
  };

  const processData = (jsonData: any[]) => {
    // Normalize headers to lowercase
    jsonData = jsonData.map((item) =>
      Object.fromEntries(
        Object.entries(item).map(([key, value]) => [key.trim().toLowerCase(), value]),
      ),
    );

    // Filter out empty rows
    jsonData = jsonData.filter((item: any) => item['unit name']?.trim() !== '');

    if (jsonData.length === 0) {
      setIsWrongData(true);
      setIsLoading(false);
      resetFileInput();
      return;
    }

    const errors: string[] = [];
    const nameSet = new Set<string>();
    const existingNames = new Set(existingUnits.map((u) => u.name.toLowerCase()));

    jsonData.forEach((item, index) => {
      const rowNumber = index + 2; // +2: 0-based index + header row

      // Check unit name is present
      if (!item['unit name'] || item['unit name'].trim() === '') {
        errors.push(`Row ${rowNumber}: Missing required field "Unit Name"`);
        return;
      }

      const unitName = item['unit name'].trim();
      const unitNameLower = unitName.toLowerCase();

      // Check for duplicates within the file
      if (nameSet.has(unitNameLower)) {
        errors.push(`Row ${rowNumber}: Duplicate unit name "${unitName}"`);
      } else {
        nameSet.add(unitNameLower);
      }

      // Check for duplicates with existing units
      if (existingNames.has(unitNameLower)) {
        errors.push(`Row ${rowNumber}: Unit "${unitName}" already exists in this property`);
      }
    });

    if (errors.length === 0) {
      const units = jsonData.map((item: any) => ({
        name: item['unit name'].trim(),
        unit_flags: [],
      }));

      setIsWrongData(false);
      onSubmit({ structure_id: structureId, units }, fileType)
        .then(() => {
          setIsLoading(false);
          handleClose();
        })
        .catch((error) => {
          setIsLoading(false);
          resetFileInput();
          if (error?.response?.data?.message) {
            setValidationErrors([`API Error: ${error.response.data.message}`]);
          } else if (error?.message) {
            setValidationErrors([`Error: ${error.message}`]);
          } else {
            setValidationErrors(['An unexpected error occurred while uploading']);
          }
          setIsWrongData(true);
        });
    } else {
      setValidationErrors(errors);
      setIsWrongData(true);
      setIsLoading(false);
      resetFileInput();
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          '.MuiOutlinedInput-root': {
            borderRadius: '14px !important',
            minHeight: '45px !important',
            padding: '0 !important',
          },
          '.MuiOutlinedInput-input': {
            padding: '8px 12px !important',
            lineHeight: '1.5em',
          },
          '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 2px rgba(71,144,204,0.2) !important',
          },
        }}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth={'sm'}
        fullWidth
        classes={{
          paper: classes.paper,
        }}
      >
        <DialogTitle
          id="form-dialog-title"
          sx={{ margin: 'auto', textAlign: 'center', paddingTop: 5 }}
        >
          <Stack direction={'column'}>
            <Box sx={{ fontSize: '42px', fontWeight: 'bold' }}>
              {isWrongData ? 'Error importing units' : 'Import new units'}
            </Box>
            <Box sx={{ fontSize: '18px' }}>
              {isWrongData
                ? 'We tried uploading your file, but received errors.'
                : 'Import a list of new units into this property'}
            </Box>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey', marginTop: 2 }} />
        <DialogContent sx={{ margin: 3 }}>
          {!isWrongData && <RenderInfo />}
          {isWrongData && <RenderWarningInfo validationErrors={validationErrors} />}
        </DialogContent>
        <Divider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey' }} />
        <DialogActions sx={{ justifyContent: 'space-between !important', margin: 2 }}>
          <SWLoadingButtonOutlined onClick={handleClose}>Cancel</SWLoadingButtonOutlined>
          <>
            <LoadingButton
              loading={isLoading}
              variant="contained"
              color="primary"
              onClick={handleButtonClick}
            >
              Upload Spreadsheet
            </LoadingButton>
            <input
              aria-label="upload spreadsheet"
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </>
        </DialogActions>
      </Dialog>
    </>
  );
};

const RenderInfo = () => {
  const handleTemplateDownload = (e: React.MouseEvent) => {
    e.preventDefault();

    fetch('/units_template.csv')
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'units_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error('Failed to download template:', err);
        window.location.href = '/units_template.csv';
      });
  };

  return (
    <Stack spacing={3}>
      <Box>
        <span style={{ fontWeight: 'bold' }}>
          1.{' '}
          <a
            href="/units_template.csv"
            download="units_template.csv"
            onClick={handleTemplateDownload}
          >
            Download this template.
          </a>
        </span>{' '}
        This pre-formatted spreadsheet is designed to ensure your units are accurately imported.
      </Box>
      <Box>
        <span style={{ fontWeight: 'bold' }}>2. Add your unit names.</span> Fill out the spreadsheet
        with one unit name per row.
        <Box paddingLeft={2} paddingTop={1}>
          <ul style={{ margin: 0 }}>
            <li>Unit Name *</li>
          </ul>
        </Box>
      </Box>
      <Box>
        <span style={{ fontWeight: 'bold' }}>3. Save and Upload.</span> Once you have filled out the
        spreadsheet, save the file as CSV or XLSX format.
        <Box paddingLeft={2} paddingTop={1} sx={{ fontSize: '0.9em', color: 'text.secondary' }}>
          <strong>Mac Numbers users:</strong> Go to File → Export To → CSV or Excel to save in a
          compatible format.
        </Box>
        Return to this page and click on the "Upload Spreadsheet" button.
      </Box>
    </Stack>
  );
};

const RenderWarningInfo = ({ validationErrors }: { validationErrors: string[] }) => (
  <Stack spacing={3}>
    {validationErrors.length > 0 ? (
      <Box>
        <Box sx={{ fontWeight: 'bold', marginBottom: 1 }}>
          Found {validationErrors.length} error{validationErrors.length > 1 ? 's' : ''} in your
          file:
        </Box>
        <Box
          sx={{
            maxHeight: 300,
            overflowY: 'auto',
            backgroundColor: '#f5f5f5',
            padding: 2,
            borderRadius: 1,
            border: '1px solid #ddd',
          }}
        >
          {validationErrors.map((error, index) => (
            <Box key={index} sx={{ marginBottom: 0.5, fontSize: '14px', color: '#d32f2f' }}>
              • {error}
            </Box>
          ))}
        </Box>
      </Box>
    ) : (
      <Box>
        <Box>Please check the data in your spreadsheet. Here are a few common issues...</Box>
        <Box paddingLeft={2} paddingTop={1}>
          <ol style={{ margin: 0 }}>
            <li>Make sure "Unit Name" is not empty for any row</li>
            <li>Check for duplicate unit names in your file</li>
            <li>Make sure the file is a CSV or XLSX</li>
          </ol>
        </Box>
      </Box>
    )}
    <Box>Still need help? Contact support@simplesubwater.com</Box>
  </Stack>
);

export default FormBulkUnit;
